import { FC } from 'react';
import { Progress, Upload } from 'antd';
import { InboxOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';

import EPTag from '@/comp/common/EPTag';
import { useStores } from '@/hooks/use-stores';
import { useImageCompression } from '@/comp/Forms/SiteSurveyForm/RoomConfigurationStep/FormComponents/ImageUpload/hooks';
import {
  ALLOWED_IMAGE_UPLOAD_FILES,
  MAX_IMAGE_UPLOAD_COUNT,
  MAX_IMAGE_UPLOAD_SIZE_MB,
} from '@/comp/Forms/SiteSurveyForm/RoomConfigurationStep/FormComponents/ImageUpload/ImageUpload.consts';
import { getImageTagDataByDeviceType } from '@/constants/CompressorImageTagData.const';

import { StyledStep } from './ImageSelectDesktop.styles';

export const ImageSelectDesktop: FC = observer(() => {
  const { imageUploadStore, mapBuilderStore } = useStores();
  const { compressImage, isLoading, totalFiles, completedFiles, showFileLimitError } = useImageCompression({
    onComplete: imageUploadStore.goToNextStep,
  });
  const { t } = useTranslation();

  return (
    <StyledStep>
      <p className="step-content-helper-text">{t('uploadImageStepHelperText')}</p>
      {isLoading ? (
        <div className="loader-container">
          <Progress status="active" percent={Math.floor((completedFiles / totalFiles) * 100)} />
        </div>
      ) : (
        <Upload.Dragger
          className="step-content-file-upload"
          name="file-upload"
          accept={ALLOWED_IMAGE_UPLOAD_FILES}
          showUploadList={false}
          beforeUpload={compressImage}
          multiple
          disabled={imageUploadStore.noEditPermission}
        >
          {showFileLimitError ? (
            <>
              <p className="upload-icon upload-error-icon">
                <InfoCircleOutlined />
              </p>
              <p className="upload-text">{t('uploadImageFileLimitErrorMessage', { limit: MAX_IMAGE_UPLOAD_COUNT })}</p>
            </>
          ) : (
            <>
              <p className="upload-icon upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="upload-text">{t('uploadImageComputerHelperText')}</p>
              <p className="upload-hint">
                {t('uploadImageRules', { limit: MAX_IMAGE_UPLOAD_COUNT, maxSize: MAX_IMAGE_UPLOAD_SIZE_MB })}
              </p>
            </>
          )}
        </Upload.Dragger>
      )}
      <div className="step-content-tag-list">
        <p className="step-content-tag-list-title">Try to include pictures of:</p>
        <div className="step-content-tag-list-content">
          {getImageTagDataByDeviceType(mapBuilderStore.selectedDevice?.deviceType).map(({ tag, translationKey }) => (
            <EPTag key={tag} label={t(translationKey)} className="step-content-image-tag" />
          ))}
        </div>
      </div>
    </StyledStep>
  );
});
